/**
 * A team's problems, as badges (the list) and as one line each (the team page).
 *
 * The server decides what is a problem; this only renders it. Leavers are why
 * the area exists (2 Oct), so a team with none shows a quiet "No problems"
 * rather than nothing — an empty cell reads as "not checked".
 */

import { Icon, TriangleAlert } from "@norican/icons";
import { Alert, Badge, Typography } from "@norican/react";

import { useT } from "@/i18n";
import type { TeamProblem } from "@/lib/api";

import {
  badgeText,
  problemBadgeKey,
  problemBadges,
  problemLineKey,
  problemTone,
} from "./lib/team-problems";

export function TeamProblemBadges({ problems }: { problems: readonly TeamProblem[] }) {
  const { t } = useT();
  const badges = problemBadges(problems);
  if (badges.length === 0) {
    return (
      <Typography variant="caption" tone="muted">
        {t("teams.noProblems")}
      </Typography>
    );
  }
  return (
    <span className="flex flex-wrap gap-1">
      {badges.map((badge) => (
        <Badge
          key={badge.code}
          tone={problemTone(badge.code)}
          variant="subtle"
          size="sm"
          className="gap-1"
        >
          <Icon icon={TriangleAlert} size="xs" />
          {badgeText(t(problemBadgeKey(badge.code)), badge.count)}
        </Badge>
      ))}
    </span>
  );
}

/** Every problem spelled out, leavers by name, above the team's details. */
export function TeamProblemList({ problems }: { problems: readonly TeamProblem[] }) {
  const { t } = useT();
  if (problems.length === 0) {
    return null;
  }
  const urgent = problems.some((problem) => problemTone(problem.code) === "danger");
  return (
    <Alert tone={urgent ? "danger" : "warning"} title={t("teams.problemsTitle")}>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {problems.map((problem, index) => (
          <li key={`${problem.code}-${problem.employee_id ?? index}`}>
            {t(problemLineKey(problem.code), { name: problem.employee_name ?? "" })}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
